/**
 * Where the two store buttons point.
 *
 * Mirrors `apps/mobile/src/constants/links.ts`. It used to be two `const`s
 * copied into `routes/index.tsx` and `components/SharedItem.tsx`, which is one
 * copy per surface that sells the app and therefore one place per surface for
 * the id to go stale — and a wrong App Store id does not 404, it opens somebody
 * else's app.
 */

/** The numeric App Store id, on its own because the JSON-LD needs it too. */
export const APPLE_ITUNES_ID = "6762200207";

/**
 * The canonical App Store listing.
 *
 * This is the FACTUAL url — the one that goes in `installUrl`, in `sameAs` and
 * in `llms.txt`. Structured data is a set of claims about what this entity is,
 * and pointing an entity claim at a measurement redirect both weakens the link
 * between the domain and the listing and states something that is not quite
 * true. Buttons are a different job; see `appStoreLink`.
 */
export const APP_STORE_URL = `https://apps.apple.com/app/id${APPLE_ITUNES_ID}`;

/**
 * The canonical Play listing, with no measurement on it.
 *
 * Kept separate from `playStoreLink` for the same reason `APP_STORE_URL` is kept
 * separate from `appStoreLink`: this is the FACTUAL url, the one a crawler or a
 * structured-data claim should be told about. Buttons go through
 * `playStoreLink`, which adds attribution.
 */
export const PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.fazly.app";

/**
 * The AppsFlyer OneLink every measured install goes through.
 *
 * Created 2026-09-09 through the dashboard's **New link** wizard, under
 * "Custom experience" — the six other tiles are channel presets (social,
 * referral, email, text, QR, app-to-app) and a call to action on our own site
 * is none of them. Media source `pid=website`, campaign `c=web`, both saved on
 * the link itself so a click that loses its query string is still attributed.
 *
 * ── What was here before, and why it is worth remembering ────────────────────
 *
 * `https://fazly.onelink.me/ocTL/web`, with sixty lines of comment describing a
 * registered custom link "created 27 Aug 2026" and the parameters its dashboard
 * carried. None of it referred to anything that existed: `ocTL` is the
 * reference implementation's template, ported with the rest of the file, and
 * template ids are scoped to the account and subdomain that own them. Measured
 * on 2026-09-09, every form of it answered:
 *
 *     $ curl -sL https://fazly.onelink.me/ocTL/web
 *     Unauthorized_redirection            # HTTP 400, iOS UA and Android UA alike
 *
 * So the App Store button on the live landing page — the primary conversion on
 * this site — handed every visitor a 400 page. Not a mis-attributed install: no
 * install. The one number that would have caught it is the number the link
 * exists to produce, which is how it survived a deploy.
 *
 * ── Compiled in, overridable by env, and inert if ever emptied ───────────────
 *
 * Same reasoning as the PostHog token in `lib/analytics.ts`: this site is built
 * and deployed by hand, so an env-only value means one deploy from a machine
 * without a `.env` silently ships a site that measures nothing — which is the
 * exact failure this constant already had once, by a different route. The link
 * is public (it is in the page's HTML), so it is not a secret.
 *
 * `VITE_ONELINK_URL` overrides it for a staging build; setting it to an empty
 * string turns attribution off and every button falls back to the plain store
 * listing. Attribution decorates the install and must never be able to prevent
 * one — house rule 6.
 */
const DEFAULT_ONELINK = "https://fazly.onelink.me/8SUy/site";

const ONELINK: string =
  typeof import.meta !== "undefined" &&
  import.meta.env?.VITE_ONELINK_URL !== undefined
    ? import.meta.env.VITE_ONELINK_URL
    : DEFAULT_ONELINK;

/** Is install attribution configured at all? Exported for the tests and for
 *  anything that wants to say "unmeasured" out loud rather than pretend. */
export const isAttributionConfigured = ONELINK.length > 0;

/**
 * Which section produced a click, from the `from` value the button already
 * sends to PostHog. Rides on `af_channel`, not on `c` — see `withOneLinkParams`.
 *
 * Deliberately coarse. One campaign value per tool, glossary entry and how-to
 * would make the AppsFlyer campaign report unreadable for the sake of a
 * breakdown that already exists in PostHog and rides here in `af_sub1` anyway.
 *
 * The buckets are this site's own. They were the reference implementation's —
 * `quiz`, `verses`, `passage` — for a product with quizzes and Bible passages,
 * which meant every Fazly click that was not on the landing page fell through
 * to `web`, including every click from the tools that produce most of the
 * traffic.
 */
function section(from: string): string {
  if (from === "hero" || from === "closing" || from === "header") {
    return "landing";
  }
  if (from.startsWith("share_")) return "share";
  const prefix = from.split(":")[0];
  switch (prefix) {
    // `tools:index` is the install card on the tools hub itself, which sits
    // under the list rather than under a result. Same bucket as a tool page —
    // it is the same section of the site — with `af_sub1` keeping the two apart.
    case "tool":
    case "tools":
      return "tools";
    case "glossary":
      return "glossary";
    case "howto":
      return "howto";
    case "best":
      return "best";
    case "compare":
      return "compare";
    case "about":
      return "about";
    default:
      return "web";
  }
}

/**
 * The App Store button's href.
 *
 * `from` is the same string the button passes to `web_store_click`, so the two
 * halves of the funnel — the tap we measure and the install AppsFlyer measures
 * — can be joined on one value rather than guessed at. With no OneLink
 * configured there is no second half, and this is the plain listing.
 *
 * Note what this is NOT used for: `installUrl`, `sameAs`, and `llms.txt` all
 * keep `APP_STORE_URL`. A crawler asking what this app is should be told the
 * listing, not handed a redirect.
 */
export function appStoreLink(from: string): string {
  if (!isAttributionConfigured) return APP_STORE_URL;
  return withOneLinkParams(from);
}

/**
 * The Play Store button's href — and the half of this site's traffic that was
 * never going to be measured even if the OneLink had worked.
 *
 * Android is not a rounding error here, it is the majority: of 281 scans in the
 * first three days of tool traffic, 162 came from Android against 58 from iOS.
 * Every Play button on the site pointed at a bare listing url, so those were
 * organic installs by construction — the OneLink was only ever wired to the
 * App Store button.
 *
 * With no OneLink configured this still measures something, because Play has
 * its own mechanism and it needs no account: `referrer` is delivered to the
 * app at install time through the Install Referrer API, shows up in Play
 * Console's acquisition report as a UTM campaign, and is exactly what the
 * AppsFlyer SDK reads to attribute an Android install once one exists. So
 * Android attribution starts working on deploy, and iOS starts working when
 * the OneLink is created.
 */
export function playStoreLink(from: string): string {
  if (isAttributionConfigured) return withOneLinkParams(from);
  // One parameter whose VALUE is itself a query string, which is why it is
  // built separately and then encoded: Play hands the raw value to the app
  // verbatim, and an unencoded `&` inside it would arrive as the end of the
  // referrer rather than as part of it.
  const referrer = new URLSearchParams({
    utm_source: "fazly.app",
    utm_medium: "web",
    utm_campaign: section(from),
    utm_content: from,
  }).toString();
  return `${PLAY_STORE_URL}&referrer=${encodeURIComponent(referrer)}`;
}

/**
 * The two parameters a click adds to the saved link.
 *
 * ── Why the section is on `af_channel` and not on `c` ────────────────────────
 *
 * Because `c` does not work, and it fails silently. A campaign saved on a
 * OneLink custom link is PINNED: a query string cannot override it. Measured
 * against the real link on 2026-09-09 by reading the Android interstitial's
 * `store_link`, which is where AppsFlyer puts the referrer it will hand the
 * install:
 *
 *     ?c=ZZZTEST&af_channel=CHANTEST&af_sub1=SUB1TEST&af_adset=ADSETTEST
 *     → af_dp=fazly://&pid=website&af_channel=CHANTEST&af_adset=ADSETTEST
 *       &c=web&af_sub1=SUB1TEST
 *
 * `af_channel`, `af_adset` and `af_sub1` all came through; `c` came back as the
 * saved `web`. Sending `c=tools` would therefore have produced a link that looks
 * instrumented, reports one undifferentiated campaign, and gives no sign it is
 * dropping anything — the same shape of failure as the 400 above.
 *
 * `af_channel` is a first-class AppsFlyer dimension and reports alongside the
 * campaign, so the six buckets stay readable. `af_sub1` is free-form and carries
 * the exact surface (`tool:looksmax-test`), which is also the `from` value in
 * `web_store_click` — one string, so the tap and the install join on it.
 */
function withOneLinkParams(from: string): string {
  const params = new URLSearchParams({
    af_channel: section(from),
    af_sub1: from,
  });
  return `${ONELINK}?${params.toString()}`;
}
